// sitrepGenerationsYMap.js — MINSKY-16409: Yjs CRUD for situation-report generation requests
// stored in Y.Map("sitrepGenerations").
//
// Structure inside the Y.Doc:
//
//   Y.Map("sitrepGenerations")
//     └── [requestId]: Y.Map
//           ├── requestId: string
//           ├── nodeId: string|null    ← originating map embed's nodeId
//           ├── status: 'queued' | 'generating' | 'done' | 'error'
//           ├── text: string           ← accumulating/final generated markdown
//           ├── errorMessage: string
//           ├── payload: object        ← {report, units, changes, idMap} from buildSitrepPayload
//           ├── images: string[]       ← map preview data URLs. Present only while status
//           │                             is 'queued'; the server clears them on claim so the
//           │                             PNG is not retained in the collaborative document.
//           ├── createdAt: number      ← epoch ms
//           └── updatedAt: number      ← epoch ms
//
// Mirrors mapPreviewsYMap.js's CRUD-helper-module convention (get-or-create entry / write /
// read / observe) — see that file's header comment for the sibling pattern this reuses. Each
// request gets its own top-level key (a client-generated UUID) rather than being keyed by
// nodeId, since a single map embed can have multiple generation requests over its lifetime
// (unlike the single "current preview" mapPreviews tracks per node) and every past request stays
// addressable by its own id for as long as the entry remains in the map. The server clears
// `images` when it claims the request; past entries stay, the PNG data URLs do not.
//
// Ownership split (MINSKY-16409): the client (this module, via `queueSitrepGeneration`) is the
// only writer of a *new* entry, always in status 'queued'. Every subsequent write — the
// queued→generating→done|error transitions and the streamed/final markdown text — belongs to
// the ws-collaboration server's SitrepGenerationHandler, under its own dedicated transaction
// origin (see dash-api/services/ws-collaboration/src/events/sitrepGeneration.js's
// SITREP_SERVER_ORIGIN). This module's write API intentionally has no "update status"/"append
// text" function for that reason — those are server-only concerns.

import * as Y from 'yjs';

export const SITREP_GENERATION_STATUS = {
    QUEUED: 'queued',
    GENERATING: 'generating',
    DONE: 'done',
    ERROR: 'error',
};

// ---------------------------------------------------------------------------
// Entry helpers
// ---------------------------------------------------------------------------

/**
 * Read the raw Y.Map entry for a given requestId, or null if it doesn't exist.
 *
 * @param {import('yjs').Map} yMapGenerations  Root Y.Map("sitrepGenerations")
 * @param {string}            requestId
 * @returns {import('yjs').Map|null}
 */
export function getSitrepGenerationEntry(yMapGenerations, requestId) {
    if (!yMapGenerations || !requestId) return null;
    return yMapGenerations.get(requestId) || null;
}

// ---------------------------------------------------------------------------
// Write API
// ---------------------------------------------------------------------------

/**
 * Create a new queued situation-report generation request. This is the sole client-side write
 * this module exposes — see the ownership-split note in this file's header comment for why.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yMapGenerations  Root Y.Map("sitrepGenerations")
 * @param {string}            requestId        Client-generated UUID for this request.
 * @param {object}             opts
 * @param {string|null}        [opts.nodeId]    Originating map embed's nodeId.
 * @param {object}             [opts.payload]   `{report, units, changes, idMap}` payload.
 * @param {string[]}           [opts.images]    Map preview PNG data URLs (best-effort).
 */
export function queueSitrepGeneration(ydoc, yMapGenerations, requestId, opts = {}) {
    if (!ydoc || !yMapGenerations || !requestId) return;
    const {nodeId = null, payload = {}, images = []} = opts;

    ydoc.transact(() => {
        const entry = new Y.Map();
        const now = Date.now();
        entry.set('requestId', requestId);
        entry.set('nodeId', nodeId);
        entry.set('status', SITREP_GENERATION_STATUS.QUEUED);
        entry.set('text', '');
        entry.set('errorMessage', '');
        entry.set('payload', payload || {});
        entry.set('images', Array.isArray(images) ? images : []);
        entry.set('createdAt', now);
        entry.set('updatedAt', now);
        yMapGenerations.set(requestId, entry);
    }, 'sitrep-generation');
}

// ---------------------------------------------------------------------------
// Read API
// ---------------------------------------------------------------------------

/**
 * Read a plain-object snapshot of one generation request.
 *
 * @param {import('yjs').Map} yMapGenerations
 * @param {string}            requestId
 * @returns {{requestId: string, nodeId: string|null, status: string, text: string,
 *   errorMessage: string, payload: object, images: string[], createdAt: number|null,
 *   updatedAt: number|null}|null} `null` when no entry exists yet for `requestId`.
 */
export function getSitrepGeneration(yMapGenerations, requestId) {
    const entry = getSitrepGenerationEntry(yMapGenerations, requestId);
    if (!entry) return null;

    return {
        requestId: entry.get('requestId') || requestId,
        nodeId: entry.get('nodeId') ?? null,
        status: entry.get('status') || SITREP_GENERATION_STATUS.QUEUED,
        text: entry.get('text') || '',
        errorMessage: entry.get('errorMessage') || '',
        payload: entry.get('payload') || {},
        images: entry.get('images') || [],
        createdAt: entry.get('createdAt') ?? null,
        updatedAt: entry.get('updatedAt') ?? null,
    };
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to updates for a specific generation request — fires whenever any field of the
 * `requestId` entry changes (status transition, streamed text delta, error, etc). Mirrors
 * `observeMapPreview`'s observeDeep + path-filter pattern.
 *
 * @param {import('yjs').Map} yMapGenerations
 * @param {string}            requestId
 * @param {Function}          callback  () => void
 * @returns {Function}  unsubscribe function
 */
export function observeSitrepGeneration(yMapGenerations, requestId, callback) {
    if (!yMapGenerations || !requestId || typeof callback !== 'function') return () => {};

    const handler = (events) => {
        const relevant = events.some((e) =>
            (e.path.length > 0 && e.path[0] === requestId) ||
            (e.path.length === 0 && e.keysChanged?.has(requestId))
        );
        if (relevant) callback(events);
    };

    yMapGenerations.observeDeep(handler);
    // Idempotent: callers (e.g. MapNodeView) may unsubscribe both reactively — once a terminal
    // status is observed — and defensively on teardown/destroy. A second unobserveDeep() call on
    // an already-removed handler is a Yjs console.error, not a thrown exception, but there's no
    // reason to let it fire at all.
    let unsubscribed = false;
    return () => {
        if (unsubscribed) return;
        unsubscribed = true;
        yMapGenerations.unobserveDeep(handler);
    };
}
